/**
 * AI Prompt Forms Module
 * Registry-based prompt form system for type-specific AI content generation UIs.
 *
 * EMG-GQ-ENUM-CATALOG: canonical slugs = QuestionTypeEnum; labels from
 * ``interactionCatalog`` (Single Choice, Gap Typing, … — not raw slugs).
 */

import { registerPromptForm } from './registry';
import { FillInTheBlanksForm, FILL_IN_THE_BLANKS_SLUG } from './fill-in-the-blanks';
import { MultipleChoiceSetForm, MULTIPLE_CHOICE_SET_SLUG } from './multiple-choice-set';
import { MatchingForm, MATCHING_SLUG } from './matching';
import { OrderingForm, ORDERING_SLUG } from './ordering';
import { DragWordsForm, DRAG_THE_WORDS_SLUG } from './drag-the-words';
import { DefaultPromptForm } from './DefaultPromptForm';
import {
    getInteractionCanonicalSlugs,
    getInteractionDisplayInfo,
    INTERACTION_CATALOG,
} from './interactionCatalog';
import { getQuestionTypeLabel } from './questionTypeLabels';
import { isQuickCreateAllowed } from './quickCreateCatalog';
import type { PromptFormComponent } from './types';
import { registerBulkFormConfigs } from './bulk-config';
import type { BulkFormConfig } from './bulk-config';

// --- Central slug-to-form mapping with display metadata ---

interface FormSlugEntry {
    slugs: string[];
    subtypeSlugs?: string[];
    form: PromptFormComponent;
    display: { name: string; description?: string; category?: string };
}

/** Dedicated forms for types that already had rich UIs. */
const DEDICATED_FORMS: FormSlugEntry[] = [
    {
        slugs: [FILL_IN_THE_BLANKS_SLUG],
        form: FillInTheBlanksForm,
        display: { name: getQuestionTypeLabel('gap_typing', 'en'), category: 'gap_typing' },
    },
    {
        slugs: [MULTIPLE_CHOICE_SET_SLUG],
        form: MultipleChoiceSetForm,
        display: { name: getQuestionTypeLabel('scq', 'en'), category: 'scq' },
    },
    {
        slugs: [MATCHING_SLUG],
        form: MatchingForm,
        display: { name: getQuestionTypeLabel('matching_text_pairs', 'en'), category: 'matching_text_pairs' },
    },
    {
        slugs: [ORDERING_SLUG],
        form: OrderingForm,
        display: { name: getQuestionTypeLabel('ordering', 'en'), category: 'ordering' },
    },
    {
        slugs: [DRAG_THE_WORDS_SLUG],
        form: DragWordsForm,
        display: { name: getQuestionTypeLabel('gap_drag_words', 'en'), category: 'gap_drag_words' },
    },
];
const dedicatedSlugSet = new Set(DEDICATED_FORMS.flatMap((e) => e.slugs));

/** Remaining INTERACTION_TYPES use DefaultPromptForm until dedicated UI lands. */
const DEFAULT_FORM_ENTRIES: FormSlugEntry[] = INTERACTION_CATALOG.filter(
    (e) => !dedicatedSlugSet.has(e.slug)
).map((e) => ({
    slugs: [e.slug],
    form: DefaultPromptForm,
    display: { name: e.label, category: e.code },
}));

const FORM_SLUG_MAPPING: FormSlugEntry[] = [...DEDICATED_FORMS, ...DEFAULT_FORM_ENTRIES];

function registerAllForms(): void {
    FORM_SLUG_MAPPING.forEach(({ slugs, subtypeSlugs = [], form }) => {
        registerPromptForm([...slugs, ...subtypeSlugs], form);
    });
}

registerAllForms();

// Bulk modal fields — media + per-type option keys from interactionCatalog.
function registerInteractionBulkConfigs(): void {
    const configs: Record<string, BulkFormConfig> = {};
    for (const entry of INTERACTION_CATALOG) {
        configs[entry.slug] = {
            fields: entry.optionFields.map((f) => ({
                key: f.key,
                label: f.label,
                labelKey: f.labelKey,
                type: f.type,
                defaultValue: f.defaultValue,
                min: f.min,
                max: f.max,
                options: f.options,
            })),
        };
    }
    registerBulkFormConfigs(configs);
}

registerInteractionBulkConfigs();

/**
 * Returns only the canonical (top-level) slugs — one per interaction enum.
 * Use this to filter the AI mode selector dropdown.
 */
export function getCanonicalSlugs(): string[] {
    return getInteractionCanonicalSlugs();
}

/**
 * True when ``slug`` is a registered base prompt-form type (AI dropdown set).
 */
export function isCanonicalPromptSlug(slug: string): boolean {
    return getCanonicalSlugs().includes(slug);
}

/**
 * Quick Create / template catalog: legacy QT_* with purposeful Agent recipes
 * (prompt / set blueprint / speak|write task). Enum interaction slugs stay
 * in the AI dropdown (``getCanonicalSlugs``).
 */
export function filterQuickCreateOptions<
    T extends { slug: string; code?: string },
>(options: T[]): T[] {
    const enumSlugs = new Set(getCanonicalSlugs());
    return options.filter((o) => {
        if (enumSlugs.has(o.slug)) return false;
        if (isQuickCreateAllowed(o.slug)) return true;
        if (o.code && isQuickCreateAllowed(o.code)) return true;
        return false;
    });
}

/**
 * Returns display metadata for every canonical form slug.
 * Consumers (e.g. useAIContentTypes) use this to add fallback selector
 * options for slugs that the backend API does not yet return.
 */
export function getRegisteredFormDisplayInfo(locale?: string): Array<{
    slug: string;
    name: string;
    description?: string;
    category?: string;
}> {
    const bySlug = new Map(getInteractionDisplayInfo(locale).map((d) => [d.slug, d]));
    return FORM_SLUG_MAPPING.flatMap((entry) =>
        entry.slugs.map((slug) => {
            const catalog = bySlug.get(slug);
            return {
                slug,
                name: catalog?.name ?? getQuestionTypeLabel(slug, locale) ?? entry.display.name,
                description: entry.display.description,
                category: catalog?.category ?? entry.display.category,
            };
        })
    );
}

// I18n
export {
    setPromptLocale,
    getPromptLocale,
    getPromptTranslations,
    usePromptTranslations,
    mediaOptionLabel,
} from './i18n';
export type { PromptTranslations } from './i18n';

// --- Public exports ---

export type {
    PromptFormData,
    PromptFormProps,
    PromptFormComponent,
    UploadedDocument,
    PresetButton,
    QuestionTypeOption,
    JobDetailsSummary,
    JobDetailsStatus,
} from './types';

export {
    PromptDescriptionInput,
    JobDetailsBar,
    QuickCreateButton,
    QuickCreateDialog,
} from './components';
export type {
    PromptDescriptionInputProps,
    JobDetailsBarProps,
    QuickCreateButtonProps,
    QuickCreateDialogProps,
} from './components';

export { registerPromptForm, getPromptForm, hasPromptForm, getRegisteredSlugs } from './registry';

export { PromptFormRenderer } from './PromptFormRenderer';

export { DefaultPromptForm } from './DefaultPromptForm';

export { FillInTheBlanksForm, FILL_IN_THE_BLANKS_SLUG } from './fill-in-the-blanks';
export type {
    FITBMediaType,
    FITBSourceType,
    FillInTheBlanksFormState,
} from './fill-in-the-blanks';

export { MultipleChoiceSetForm, MULTIPLE_CHOICE_SET_SLUG } from './multiple-choice-set';
export type {
    MCQSetMediaType,
    MCQSetSourceType,
    MultipleChoiceSetFormState,
} from './multiple-choice-set';

export { MatchingForm, MATCHING_SLUG } from './matching';
export type { MatchingMediaType, MatchingSourceType, MatchingFormState } from './matching';

export { OrderingForm, ORDERING_SLUG } from './ordering';
export type { OrderingMediaType, OrderingSourceType, OrderingFormState } from './ordering';

export { DragWordsForm, DRAG_THE_WORDS_SLUG } from './drag-the-words';
export type {
    DragWordsMediaType,
    DragWordsSourceType,
    DragWordsFormState,
} from './drag-the-words';

export {
    getBulkFormConfigs,
    registerBulkFormConfigs,
    MEDIA_FIELD,
    NUM_BLANKS_FIELD,
} from './bulk-config';
// EAA-2026: semantic layout of the bulk dialog's per-type settings.
export {
    BULK_FIELD_LAYOUT,
    BULK_FIELD_UNIT,
    buildBulkFieldLayout,
    invalidRangeKeys,
} from './bulk-layout';
export type {
    BulkCommonSlot,
    BulkCompoundKind,
    BulkFieldLayout,
    BulkFieldLayoutRule,
    BulkFieldSection,
    BulkLayoutCell,
} from './bulk-layout';
export type {
    BulkFormConfig,
    BulkFormField,
    BulkFormFieldType,
    BulkFormFieldOption,
} from './bulk-config';

export {
    INTERACTION_CATALOG,
    getInteractionCanonicalSlugs,
    getInteractionDisplayInfo,
    getInteractionEntry,
} from './interactionCatalog';
export {
    buildDefaultInteractionOptions,
    useInteractionOptions,
    resolveQuestionTypeDisplayName,
    getInteractionOptionFields,
} from './interactionOptions';
export type {
    InteractionEnumSlug,
    InteractionCatalogEntry,
    InteractionOptionField,
} from './interactionCatalog';

export {
    QUESTION_TYPE_LABEL_CATALOG,
    QUICK_CREATE_TYPE_LABELS,
    getQuestionTypeColor,
    getQuestionTypeLabel,
    getQuestionTypeLabelCatalog,
    hasQuestionTypeLabel,
    isQuestionTypeEnumSlug,
    normalizeQuestionTypeLocale,
} from './questionTypeLabels';
export type {
    QuestionTypeEnumSlug,
    QuestionTypeLocale,
    QuestionTypeLabelEntry,
} from './questionTypeLabels';

// EAA-2026: tenant question-type groups replace the retired skill/category table.
export { groupQuestionTypes, OTHER_QUESTION_TYPE_GROUP_KEY } from './questionTypeGroups';
export {
    filterQuestionTypeGroups,
    normalizeSearchText,
    questionTypeMatches,
    questionTypeSearchTexts,
} from './questionTypeSearch';
export type { SearchableQuestionType } from './questionTypeSearch';
export type {
    GroupedQuestionTypes,
    QuestionTypeGroupInput,
    QuestionTypeGroupMemberRef,
} from './questionTypeGroups';

export {
    QUICK_CREATE_CODES,
    quickCreateCodeToSlug,
    isQuickCreateAllowed,
    getQuickCreateSlugs,
} from './quickCreateCatalog';
export type { QuickCreateCode } from './quickCreateCatalog';

export { useGenerationHistory } from './hooks/useGenerationHistory';
export type {
    UseGenerationHistoryReturn,
    GenerationHistoryApi,
    GenerationHistoryMode,
    ContentVersionDTO,
} from './hooks/useGenerationHistory';

export { ChatLayout, ChatHistory, ChatMessage, ChatInput } from './components/chat';
export type {
    ChatLayoutProps,
    ChatHistoryProps,
    ChatMessageProps,
    ChatInputProps,
    ChatMessageData,
    ChatJobStatus,
} from './components/chat';
